import { Avatar } from '@dealstack/frontend/shared/components/Avatar/Avatar';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Flex, Text, FlexProps } from '@mantine/core';
import React from 'react';
import styled from 'styled-components';

const MAX_ITEM = 10;

interface ListItemValue {
	top?: string;
	bottom: string;
}

interface ListItem {
	id: string;
	value: ListItemValue;
	onClick?: () => void;
}

interface Props {
	title: React.ReactNode | string;
	items: ListItem[];
	button?: React.ReactNode;
	children?: React.ReactNode;
	maxItems?: number;
	moreItemsClick?: () => void;
}

const CardTitle = ({ title, button }: { title: Props['title']; button: Props['button'] }) => {
	return typeof title === 'string' ? (
		<Flex
			justify="space-between"
			p="1rem"
			bg="gray.0"
			sx={(theme) => ({
				border: `1px solid ${theme.colors.gray[2]}`,
				borderTopRightRadius: theme.radius.lg,
				borderTopLeftRadius: theme.radius.lg,
			})}
		>
			<Flex gap="0.5rem" align="center">
				<Text data-testid={`card-title-${title}`} size="B6Bold" c="dark.8">
					{title}
				</Text>
			</Flex>
			{button}
		</Flex>
	) : (
		<>{title}</>
	);
};

const CardListItem = ({
	children,
	avatarChildren,
	onClick,
}: {
	children: React.ReactNode;
	avatarChildren: React.ReactNode;
	onClick?: () => void;
}) => {
	return (
		<Item
			gap="0.5rem"
			p="0.5rem"
			data-testid={TEST_ELEMENT_IDS.MINI_LIST_CARD_ITEM}
			onClick={onClick}
			$isActionable={!!onClick}
		>
			<Avatar size="l" children={avatarChildren}></Avatar>
			<Flex direction="column" justify="center" style={{ flex: 1, overflow: 'hidden' }}>
				{children}
			</Flex>
		</Item>
	);
};

const UserListItem = ({ item }: { item: ListItem }) => {
	return (
		<CardListItem
			key={item.id}
			avatarChildren={
				item.value.top ? item.value.top : <Icon variant="user-profile" size="xl" />
			}
			onClick={item.onClick}
			children={
				<>
					<Text size="B6Medium" truncate c="dark.9">
						{item.value.top}
					</Text>
					<Text size="B6Medium" truncate c={item.value.top ? 'dark.0' : 'dark.9'}>
						{item.value.bottom}
					</Text>
				</>
			}
		/>
	);
};

const CardList = ({
	items,
	children,
	maxItems,
	moreItemsClick,
}: {
	items: Props['items'];
	children: Props['children'];
	moreItemsClick: Props['moreItemsClick'];
	maxItems: number;
}) => {
	const tCommonUi = useTranslationCommonUi();
	const displayedItems = items.slice(0, maxItems);
	const remainingItems = items.slice(maxItems);

	return (
		<Flex
			direction="column"
			align="flex-start"
			px="0.5rem"
			py="1rem"
			sx={(theme) => ({
				borderLeft: `1px solid ${theme.colors.gray[2]}`,
				borderRight: `1px solid ${theme.colors.gray[2]}`,
				borderBottom: `1px solid ${theme.colors.gray[2]}`,
				borderBottomRightRadius: theme.radius.lg,
				borderBottomLeftRadius: theme.radius.lg,
			})}
			style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
		>
			<Flex direction="column" w="100%" mih={0} style={{ flex: 1, overflowY: 'auto' }}>
				{displayedItems.map((item) => (
					<UserListItem key={item.id} item={item} />
				))}
			</Flex>
			{!!remainingItems.length && (
				<CardListItem
					avatarChildren={<Icon variant="people-users" size="xl" />}
					onClick={moreItemsClick}
					children={
						<Text size="B6Bold" c="blue.6">
							{remainingItems.length} {tCommonUi('others')}
						</Text>
					}
				/>
			)}
			{children}
		</Flex>
	);
};

/**
 * Please use this component if the list is small and lacks pagination
 */

export const MiniListCard = ({
	title,
	items,
	children,
	button,
	maxItems = MAX_ITEM,
	moreItemsClick,
}: Props) => {
	return (
		<Flex direction="column" w="100%" mih={0} style={{ flex: 1 }}>
			<CardTitle title={title} button={button} />
			<CardList
				items={items}
				children={children}
				maxItems={maxItems}
				moreItemsClick={moreItemsClick}
			/>
		</Flex>
	);
};

const Item = styled(Flex)<
	FlexProps & {
		$isActionable: boolean;
	}
>`
	border: solid 1px transparent;
	border-radius: 4px;
	opacity: 0.8;
	width: 100%;
	${Avatar} {
		border: solid 1px transparent;
	}
	${({ $isActionable, theme }) =>
		$isActionable
			? `
				:hover {
					border-color: ${theme.v3.colors.gray[2]};
					background-color: ${theme.v3.colors.blue[0]};
					cursor: pointer;

					${Avatar} {
						border-color: ${theme.v3.colors.white[0]};
					}
				}
			`
			: ''}
`;
